import { useState } from 'react';
import LogoLockup from '../chambers/LogoLockup';
import LegalModal from '../legal/LegalModal';
import GoogleGlyph from './landing/GoogleGlyph';

/**
 * Sign-in screen for the INSTALLED app (home-screen / PWA launch). Someone
 * who installed iAdvocate has already chosen it — they need the door, not the
 * 20-screen marketing page the browser landing shows. Same chambers palette
 * and the same Google sign-in as the landing; nothing here is a second auth
 * path.
 *
 * Trial line mirrors PricingBand's "The trial, in plain terms" — keep the two
 * in step if the trial terms change.
 */
interface InstalledSignInProps {
  onLogin: () => void;
  loading: boolean;
  error: Error | null;
}

export default function InstalledSignIn({ onLogin, loading, error }: InstalledSignInProps) {
  const [openDoc, setOpenDoc] = useState<'terms' | 'privacy' | null>(null);

  return (
    <main className="min-h-[100dvh] bg-chamber-rail font-instrument flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-sm flex flex-col items-center text-center">
        <LogoLockup />

        <h1 className="mt-12 font-display font-normal text-[34px] leading-[1.1] text-parchment">
          Your chambers,
          <em className="block font-editorial italic text-brass">AI-powered.</em>
        </h1>
        <p className="mt-4 text-[15px] leading-relaxed text-chamber-muted-bright">
          Sign in to reach your matters, research and hearings.
        </p>

        {error && (
          <div
            role="alert"
            className="mt-6 w-full border border-crimson-500/40 bg-crimson-950/30 px-4 py-3 text-sm text-crimson-300 text-left"
          >
            {error.message}
          </div>
        )}

        <button
          type="button"
          onClick={onLogin}
          disabled={loading}
          className="mt-10 w-full min-h-[52px] flex items-center justify-center gap-3 bg-parchment text-chamber-ink text-[15px] font-semibold hover:bg-parchment-hover transition-colors disabled:opacity-50 cursor-pointer"
        >
          <GoogleGlyph />
          Continue with Google
        </button>
        <p className="mt-4 text-[13px] text-chamber-muted">
          New here? 30 days on Pro, no card.
        </p>
      </div>

      <div className="mt-14 flex items-center gap-2 text-[13px] text-chamber-muted">
        <button
          type="button"
          onClick={() => setOpenDoc('terms')}
          className="min-h-[44px] px-2 hover:text-brass transition-colors"
        >
          Terms
        </button>
        <span aria-hidden="true">·</span>
        <button
          type="button"
          onClick={() => setOpenDoc('privacy')}
          className="min-h-[44px] px-2 hover:text-brass transition-colors"
        >
          Privacy
        </button>
      </div>

      {openDoc && <LegalModal initialDoc={openDoc} onClose={() => setOpenDoc(null)} />}
    </main>
  );
}
